import React from "react";
import { FeatherAlertCircle, FeatherCheck } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";

interface SettingsPanelProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  /** Page-level failure message. */
  error?: string | null;
  /** Shown once after a successful save. */
  saved?: boolean;
  /** Confirmation wording. Defaults to the Company Details panel's. */
  savedMessage?: string;
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

/**
 * The chrome around a settings panel: heading, body, and a sticky action bar.
 *
 * The bar stays out of the way until there is something to do with it — an
 * always-visible Save on a form with no changes trains people to ignore it.
 * Once dirty it pins to the bottom so a long form never hides its own Save.
 *
 * Modelled on master-admin's `SubStepShell`, minus the wizard's Continue: this
 * is a settings page you leave when you are done, not a step you advance.
 */
export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  title,
  description,
  children,
  error,
  saved,
  savedMessage = "Company details saved.",
  dirty,
  saving,
  onSave,
  onDiscard,
}) => (
  <div className="flex w-full flex-col gap-8">
    <div className="flex flex-col gap-1">
      <span className="text-heading-2 font-heading-2 text-default-font">{title}</span>
      {description && (
        <span className="text-body font-body text-subtext-color">{description}</span>
      )}
    </div>

    {error && (
      <div className="flex w-full items-start gap-2 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
        <FeatherAlertCircle className="mt-0.5 shrink-0 text-body font-body text-error-700" />
        <span className="text-body font-body text-error-700">{error}</span>
      </div>
    )}

    {saved && !dirty && (
      <div className="flex w-full items-center gap-2 rounded-md border border-solid border-success-200 bg-success-50 px-4 py-3">
        <FeatherCheck className="shrink-0 text-body font-body text-success-700" />
        <span className="text-body font-body text-success-700">{savedMessage}</span>
      </div>
    )}

    <div className="flex w-full flex-col gap-8">{children}</div>

    {dirty && (
      <div className="sticky bottom-0 flex w-full items-center justify-between gap-3 border-t border-solid border-neutral-border bg-default-background py-4">
        <span className="text-caption font-caption text-subtext-color">
          You have unsaved changes.
        </span>
        <div className="flex items-center gap-2">
          <Button variant="neutral-secondary" disabled={saving} onClick={onDiscard}>
            Discard
          </Button>
          <Button loading={saving} disabled={saving} onClick={onSave}>
            Save changes
          </Button>
        </div>
      </div>
    )}
  </div>
);
